import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Patients
  getPatients: async (params = {}) => {
    const response = await axios.get(`${API_URL}/patients`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  },

  getPatient: async (patientId) => {
    const response = await axios.get(`${API_URL}/patients/${patientId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  createPatient: async (data) => {
    const response = await axios.post(`${API_URL}/patients`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updatePatient: async (patientId, data) => {
    const response = await axios.put(`${API_URL}/patients/${patientId}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Consultations
  getConsultations: async (params = {}) => {
    const response = await axios.get(`${API_URL}/consultations`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  },

  getConsultation: async (consultationId) => {
    const response = await axios.get(`${API_URL}/consultations/${consultationId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  createConsultation: async (data) => {
    const response = await axios.post(`${API_URL}/consultations`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateConsultation: async (consultationId, data) => {
    const response = await axios.put(`${API_URL}/consultations/${consultationId}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteConsultation: async (consultationId) => {
    const response = await axios.delete(`${API_URL}/consultations/${consultationId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // P4 — archive/restore endpoints
  archiveConsultation: async (consultationId) => {
    const response = await axios.post(
      `${API_URL}/consultations/${consultationId}/archive`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },
  restoreConsultation: async (consultationId) => {
    const response = await axios.post(
      `${API_URL}/consultations/${consultationId}/restore`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  saveDocumentVersion: async (consultationId, data) => {
    const response = await axios.post(
      `${API_URL}/consultations/${consultationId}/save-version`,
      data,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  checkPatientHasEvaluation: async (patientId) => {
    const response = await axios.get(
      `${API_URL}/patients/${patientId}/has-approved-evaluation`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getPatientConsultations: async (patientId, params = {}) => {
    const response = await axios.get(`${API_URL}/consultations`, {
      headers: getAuthHeader(),
      params: { patient_id: patientId, ...params }
    });
    return response.data;
  },

  // DRAFT-01 — Continuidade de rascunhos
  saveDraft: async (consultationId, clinicalDocumentation = null) => {
    const response = await axios.patch(
      `${API_URL}/consultations/${consultationId}/save-draft`,
      { clinical_documentation: clinicalDocumentation },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // P2 AUTOSAVE — working-draft persistence (optimistic concurrency)
  saveDraftContent: async (consultationId, baseRevision, draftContent) => {
    const response = await axios.put(
      `${API_URL}/consultations/${consultationId}/draft`,
      { baseRevision, draftContent },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // P3 CONSENT/LGPD — append-only consent ledger
  getConsentStatuses: async () => {
    const response = await axios.get(
      `${API_URL}/consents/status`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },
  postConsent: async (payload) => {
    const response = await axios.post(
      `${API_URL}/consents`,
      payload,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  getInProgressConsultations: async () => {
    const response = await axios.get(
      `${API_URL}/consultations/in-progress`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  generateDocumentation: async (consultationId, data) => {
    const response = await axios.post(
      `${API_URL}/consultations/${consultationId}/generate-documentation`,
      data,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  },

  // SRC-01 — Síntese de Raciocínio Clínico
  generateSrc: async (consultationId) => {
    const response = await axios.post(
      `${API_URL}/consultations/${consultationId}/generate-src`,
      {},
      { headers: getAuthHeader(), timeout: 90000 }
    );
    return response.data;
  },

  confirmSrc: async (consultationId, payload) => {
    const response = await axios.patch(
      `${API_URL}/consultations/${consultationId}/src`,
      payload,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  exportPdf: async (consultationId, exportType = 'patient') => {
    const response = await axios.get(
      `${API_URL}/consultations/${consultationId}/export-pdf`,
      {
        headers: getAuthHeader(),
        params: { export_type: exportType },
        responseType: 'blob'
      }
    );
    return response.data;
  },

  // ATTACH-01 — clinical attachments
  uploadAttachment: async (consultationId, file, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(
      `${API_URL}/consultations/${consultationId}/attachments`,
      formData,
      {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      }
    );
    return response.data;
  },

  deleteAttachment: async (consultationId, attachmentId) => {
    const response = await axios.delete(
      `${API_URL}/consultations/${consultationId}/attachments/${attachmentId}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Returns a Blob URL for inline viewing (auth header required → fetch first).
  fetchAttachmentBlobUrl: async (consultationId, attachmentId) => {
    const response = await axios.get(
      `${API_URL}/consultations/${consultationId}/attachments/${attachmentId}`,
      { headers: getAuthHeader(), responseType: 'blob' }
    );
    return window.URL.createObjectURL(response.data);
  },

  // Dashboard
  getDashboardStats: async () => {
    const response = await axios.get(`${API_URL}/dashboard/stats`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Billing
  getBillingPlans: async () => {
    const response = await axios.get(`${API_URL}/billing/plans`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  createCheckout: async (data) => {
    const response = await axios.post(`${API_URL}/billing/checkout`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getCheckoutStatus: async (sessionId) => {
    const response = await axios.get(`${API_URL}/billing/checkout/status/${sessionId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getSubscription: async () => {
    const response = await axios.get(`${API_URL}/billing/subscription`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getUsageStats: async () => {
    const response = await axios.get(`${API_URL}/billing/usage`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getCreditsBalance: async () => {
    const response = await axios.get(`${API_URL}/credits/balance`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getCreditTransactions: async (params = {}) => {
    const response = await axios.get(`${API_URL}/credits/transactions`, {
      headers: getAuthHeader(), params
    });
    return response.data;
  },

  openCustomerPortal: async () => {
    const response = await axios.post(`${API_URL}/billing/portal`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getBillingTransactions: async () => {
    const response = await axios.get(`${API_URL}/billing/transactions`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // BILLING-02 — one-time credit pack purchase
  buyCredits: async (pack, originUrl = null) => {
    const response = await axios.post(
      `${API_URL}/billing/buy-credits`,
      { pack, origin_url: originUrl || window.location.origin },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Audio Transcription
  transcribeAudio: async (fileOrFormData, onProgress = null) => {
    // Accepts either a File/Blob or a pre-built FormData (from RecordingStep)
    let formData;
    if (typeof FormData !== 'undefined' && fileOrFormData instanceof FormData) {
      formData = fileOrFormData;
    } else {
      formData = new FormData();
      formData.append('file', fileOrFormData);
    }

    const config = {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      };
    }

    const response = await axios.post(`${API_URL}/audio/transcribe`, formData, config);
    return response.data;
  },

  getSupportedAudioFormats: async () => {
    const response = await axios.get(`${API_URL}/audio/supported-formats`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Whisper Transcription
  transcribeWithWhisper: async (formData, onProgress = null) => {
    const config = {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      };
    }

    const response = await axios.post(`${API_URL}/transcription/whisper`, formData, config);
    return response.data;
  },

  getWhisperFormats: async () => {
    const response = await axios.get(`${API_URL}/transcription/whisper/formats`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // User Profile
  getProfile: async () => {
    const response = await axios.get(`${API_URL}/auth/me`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Admin endpoints
  admin: {
    getStats: async () => {
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: getAuthHeader()
      });
      return response.data;
    },

    getRecentLogins: async (limit = 5) => {
      const response = await axios.get(`${API_URL}/admin/recent-logins`, {
        headers: getAuthHeader(),
        params: { limit }
      });
      return response.data;
    },

    getBilling: async () => {
      const response = await axios.get(`${API_URL}/admin/billing`, {
        headers: getAuthHeader()
      });
      return response.data;
    },

    runHealthCheck: async () => {
      const response = await axios.get(`${API_URL}/admin/health`, {
        headers: getAuthHeader()
      });
      return response.data;
    },

    getUsers: async (limit = 100, skip = 0) => {
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: getAuthHeader(),
        params: { limit, skip }
      });
      return response.data;
    },

    getPatients: async (limit = 100, skip = 0) => {
      const response = await axios.get(`${API_URL}/admin/patients`, {
        headers: getAuthHeader(),
        params: { limit, skip }
      });
      return response.data;
    },

    getConsultations: async (limit = 100, skip = 0) => {
      const response = await axios.get(`${API_URL}/admin/consultations`, {
        headers: getAuthHeader(),
        params: { limit, skip }
      });
      return response.data;
    },

    getActivity: async () => {
      const response = await axios.get(`${API_URL}/admin/activity`, {
        headers: getAuthHeader()
      });
      return response.data;
    },

    exportUsers: async (format = 'csv') => {
      const response = await axios.get(`${API_URL}/admin/export/users`, {
        headers: getAuthHeader(),
        params: { format },
        responseType: 'blob'
      });
      return response;
    },

    exportPatients: async (format = 'csv') => {
      const response = await axios.get(`${API_URL}/admin/export/patients`, {
        headers: getAuthHeader(),
        params: { format },
        responseType: 'blob'
      });
      return response;
    },

    exportConsultations: async (format = 'csv') => {
      const response = await axios.get(`${API_URL}/admin/export/consultations`, {
        headers: getAuthHeader(),
        params: { format },
        responseType: 'blob'
      });
      return response;
    },

    exportAll: async () => {
      const response = await axios.get(`${API_URL}/admin/export/all`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      return response;
    },

    // --- Admin members / invites ---
    getMembers: async () => {
      const response = await axios.get(`${API_URL}/admin/members`, { headers: getAuthHeader() });
      return response.data;
    },
    inviteAdmin: async (email, fullNameHint) => {
      const response = await axios.post(`${API_URL}/admin/invites`,
        { email, full_name_hint: fullNameHint || null },
        { headers: getAuthHeader() }
      );
      return response.data;
    },
    revokeMember: async (userId) => {
      const response = await axios.delete(`${API_URL}/admin/members/${userId}`, { headers: getAuthHeader() });
      return response.data;
    },
    getAuditLogs: async (params = {}) => {
      const response = await axios.get(`${API_URL}/admin/audit-logs`, { headers: getAuthHeader(), params });
      return response.data;
    },

    // --- User management ---
    getUserDetail: async (userId) => {
      const response = await axios.get(`${API_URL}/admin/users/${userId}`, { headers: getAuthHeader() });
      return response.data;
    },
    getUserConsultations: async (userId, limit = 50, skip = 0) => {
      const response = await axios.get(`${API_URL}/admin/users/${userId}/consultations`, {
        headers: getAuthHeader(), params: { limit, skip }
      });
      return response.data;
    },
    setUserStatus: async (userId, active) => {
      const response = await axios.patch(`${API_URL}/admin/users/${userId}/status`,
        { active }, { headers: getAuthHeader() });
      return response.data;
    },
    deleteUser: async (userId) => {
      const response = await axios.delete(`${API_URL}/admin/users/${userId}`, { headers: getAuthHeader() });
      return response.data;
    }
  },

  // --- Password ---
  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    const response = await axios.post(`${API_URL}/auth/change-password`,
      { current_password: currentPassword, new_password: newPassword, confirm_password: confirmPassword },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // --- Admin invite accept (public) ---
  acceptAdminInvite: async (token, password, fullName) => {
    const response = await axios.post(`${API_URL}/admin/invites/accept`, {
      token, password, full_name: fullName
    });
    return response.data;
  },

  // AUTH-02 — Email verification
  verifyEmail: async (token) => {
    const response = await axios.get(`${API_URL}/auth/verify-email`, {
      params: { token }
    });
    return response.data;
  },

  resendVerification: async () => {
    const response = await axios.post(`${API_URL}/auth/resend-verification`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // ONBOARD-01 — Activation milestones
  completeOnboarding: async () => {
    const response = await axios.patch(`${API_URL}/auth/onboarding-complete`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  markUpgradePromptShown: async () => {
    const response = await axios.patch(`${API_URL}/auth/onboarding-prompt-shown`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // NPS-01 — Survey + referral
  npsMarkShown: async () => {
    const response = await axios.post(`${API_URL}/nps/mark-shown`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  npsRespond: async ({ score, comment }) => {
    const response = await axios.post(`${API_URL}/nps/respond`, { score, comment }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  npsDismiss: async () => {
    const response = await axios.post(`${API_URL}/nps/dismiss`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  npsReferralClicked: async ({ channel }) => {
    const response = await axios.post(`${API_URL}/nps/referral-clicked`, { channel }, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};
